/**
 * The one external script this SPA loads (task-7-brief.md Step 2):
 * https://challenges.cloudflare.com/turnstile/v0/api.js, injected on mount by
 * this component and ONLY by the login view -- never bundled, never present
 * on an authenticated page.
 *
 * It renders EXPLICITLY (`turnstile.render`) into a ref rather than the
 * implicit `data-sitekey` div, because that is what lets `Login.tsx` hold a
 * handle to the widget and call `.reset()` on it. A token is single-use: a
 * failed submit that does not reset leaves a stale token that fails every
 * retry with no visible reason.
 *
 * IF THE SCRIPT ITSELF NEVER LOADS (blocked, offline) `onUnavailable` fires
 * once. Login.tsx disables submit rather than posting a request with no
 * token -- a guaranteed 400 turnstile_required and a confusing error.
 *
 * `onToken`/`onUnavailable` are read through refs, not through the effect's
 * dependency array: Login.tsx re-renders on every keystroke of the email
 * field, and re-running this effect on each of those would destroy and
 * recreate the widget (and clear a valid token) on every keystroke rather
 * than only when `siteKey` actually changes.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ID = 'stackstock-turnstile-script';

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export interface TurnstileHandle {
  /** Clears the current token. Call after every submit -- tokens are single-use. */
  reset: () => void;
}

export interface TurnstileProps {
  siteKey: string;
  /** A fresh token, or null once it is known stale (expired, widget error). */
  onToken: (token: string | null) => void;
  /** Fires once, only if the script itself never loads. */
  onUnavailable: () => void;
}

export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(function Turnstile(
  { siteKey, onToken, onUnavailable },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null) window.turnstile?.reset(widgetIdRef.current);
    },
  }), []);

  useEffect(() => {
    let cancelled = false;

    function renderWidget() {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => { if (!cancelled) onTokenRef.current(token); },
        'expired-callback': () => { if (!cancelled) onTokenRef.current(null); },
        'error-callback': () => { if (!cancelled) onTokenRef.current(null); },
      });
    }

    /**
     * Review M-1: without this, the container `<div>` disappears (React
     * unmount) but the script's own internal widget registry keeps a stale
     * entry per round trip (email -> code -> email remounts a fresh widget
     * each time back on the email step). `try/catch` because teardown must
     * never throw -- a renamed/missing method on a third-party global must
     * not crash a view that is in the middle of unmounting.
     */
    function removeWidget() {
      if (widgetIdRef.current === null) return;
      try {
        window.turnstile?.remove(widgetIdRef.current);
      } catch {
        // Best-effort cleanup only; nothing to recover from here.
      }
      widgetIdRef.current = null;
    }

    if (window.turnstile) {
      renderWidget();
      return () => {
        cancelled = true;
        removeWidget();
      };
    }

    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }

    const handleLoad = () => { if (!cancelled) renderWidget(); };
    const handleError = () => { if (!cancelled) onUnavailableRef.current(); };
    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    return () => {
      cancelled = true;
      removeWidget();
      script?.removeEventListener('load', handleLoad);
      script?.removeEventListener('error', handleError);
    };
  }, [siteKey]);

  return <div ref={containerRef} className="turnstile-widget" />;
});
